<template>
  <div>
    <div
      v-if="invoice"
      class="sale-document-body md__border border-radius bg-color-write p-3 mb-2"
      :class="{ 'signing-card--active': isSigning }"
    >
      <div class="sale-document-info">
        <div class="sale-document-description d-flex justify-content-between">
          <div class="info cursor-point" @click.stop="openPreview">
            <p class="m-0 text-color-primary str-limit-space">
              N° {{ invoice.number_invoice }}
            </p>
            <p class="m-0 font-bold text-color-primary str-limit-space">
              {{ invoice.client.name }}
            </p>
            <p class="m-0" v-if="quote">
              <small>
                <span class="font-semi-bold mr-2"
                  >Cotización: #{{ quote.code }}-V{{
                    quoteVersion.version_number
                  }}</span
                >
              </small>
            </p>
          </div>
          <div class="sale-document-number">
            <h5 class="h5 font-bold">
              <FormatAmount :amount="invoice.total" />
            </h5>
            <small
              v-if="invoice.payment_status === 'partial'"
              class="d-block text-right"
              :style="{
                color: hasOutstandingBalance ? '#e07b00' : '#9aa0b3',
                fontWeight: hasOutstandingBalance ? 600 : 500,
                marginTop: '-2px'
              }"
            >
              Saldo: <FormatAmount :amount="invoice.balance" />
            </small>
            <span class="card-date">{{
              formatFullDate(invoice.emission_date || invoice.created_at)
            }}</span>
          </div>
        </div>
      </div>

      <div
        class="sale-document-actions d-flex justify-content-between align-items-center mt-4"
      >
        <div class="status d-flex align-items-center">
          <template v-if="isSigning">
            <span class="signing-badge">
              <span class="signing-badge__spinner"></span>
              Firmando...
            </span>
          </template>
          <StatusReadDocumentsComponent
            v-else
            :status="invoice.status"
            typeStatus="short"
          />
          <CheckSuccessfully
            v-if="invoice.is_paid || invoice.payment_status === 'paid'"
            class="ml-2"
            :success="true"
            text="Pagada"
          />
          <el-tag
            v-else-if="invoice.payment_status === 'partial'"
            class="ml-2 border-radius"
            type="warning"
            size="small"
            effect="light"
            style="font-weight: 700;"
          >
            Abono
          </el-tag>
          <div class="ml-2">
            <ImageIconWoocommerce :urlImage="invoice.woocomerce_id" />
            <small
              ><span class="font-semi-bold mr-2">
                {{ invoice.woocomerce_id }}</span
              ></small
            >
          </div>
          <div
            class=""
            v-if="
              invoice.method_payment == 2 &&
                !invoice.is_paid &&
                invoice.status !== 'nuled'
            "
          >
            <small>
              <el-tag
                :type="invoice.is_credit_expired ? 'danger' : 'default'"
                :class="`status border-radius`"
                style=" font-size: 10px;"
                round
              >
                <span class="">
                  Exp: {{ invoice.limit_credit_at_formatted }}</span
                >
              </el-tag>
            </small>
          </div>
          <el-tooltip
            v-if="
              invoice.enable_reminders &&
                !invoice.is_paid &&
                invoice.status !== 'nuled'
            "
            :content="reminderTooltip"
            placement="top"
            effect="light"
          >
            <span class="reminder-badge ml-2">
              <i class="el-icon-bell"></i>
            </span>
          </el-tooltip>
        </div>
        <div class="next" @click.stop>
          <InvoiceOptionsByStatus
            ref="optionsMenu"
            :status="invoice.status"
            :id="invoice.id"
            :is_paid="invoice.is_paid"
            :emission_date="invoice.emission_date"
            :client_document="invoice.client ? invoice.client.document : null"
            @goPage="goRouter"
            @payLink="getInvoicePaymentLink"
            @onUpdate="onUpdate"
            @onSigningStart="onSigningStart"
            @onSigningDone="onSigningDone"
            @onSigningError="onSigningError"
            @onDocDeleted="onDocDeleted"
          />
        </div>
      </div>
      <WrapperPaymentLink
        :paymentLink="paymentLink"
        :showDialog.sync="showDialogPayLink"
        @onDialogClose="onDialogPayLinkClose"
      />
    </div>
    <PaymentUploadDocument
      :visible.sync="showEnablePaylink"
      @onClose="onClosePaymentUpDoc"
      v-if="showEnablePaylink"
    />
  </div>
</template>

<script>
import StatusReadDocumentsComponent from "../../../../components/StatusReadDocuments";
import CheckSuccessfully from "@/components/General/CheckSuccessfully";
import WrapperPaymentLink from "../../../components/WrapperPaymentLink";
import FormatAmount from "@/components/General/FormatAmount";
import PaymentUploadDocument from "../../payments/components/PaymentUploadDocument";
import ImageIconWoocommerce from "../../../../components/ImageIconWoocommerce";
import paymentService from "../../payments/services/paymentService";
import InvoiceOptionsByStatus from "./InvoiceOptionsByStatus.vue";

export default {
  name: "InvoiceCardComponent",
  data() {
    return {
      showDialogPayLink: false,
      paymentLink: null,
      showEnablePaylink: false,
      isSigning: false
    };
  },
  computed: {
    quote() {
      const { quote } = this.invoice;
      return quote;
    },
    quoteVersion() {
      const { quote_version } = this.invoice;
      return quote_version;
    },
    isStatusNuled() {
      return this.invoice.status === "nuled";
    },
    isStatusApproved() {
      return this.invoice.status === "success";
    },
    isPaidLink() {
      return this.invoice.isPaidLink;
    },
    reminderTooltip() {
      const next = this.invoice.next_reminder_at;
      if (!next) return "Recordatorios de cobro activos";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextDate = new Date(next + "T00:00:00");
      const diffMs = nextDate - today;
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      const formatted = this.formatFullDate(next);
      if (diffDays === 0) return `Próximo recordatorio: hoy`;
      if (diffDays === 1) return `Próximo recordatorio: mañana`;
      if (diffDays < 0) return "Recordatorios de cobro activos";
      return `Próximo recordatorio: ${formatted} (${diffDays} días)`;
    },
    isShowPayLink() {
      return (
        !this.isStatusNuled &&
        !this.invoice?.is_paid &&
        !this.isPaidLink &&
        this.isStatusApproved
      );
    },
    // Saldo real por cobrar (redondeado a 2 decimales). Si redondea a 0,
    // no se le debe nada al usuario → se muestra en gris, no en naranja.
    hasOutstandingBalance() {
      return Math.round((Number(this.invoice.balance) || 0) * 100) / 100 > 0;
    }
  },
  methods: {
    formatFullDate(date) {
      if (!date) return "";
      const normalized = /^\d{4}-\d{2}-\d{2}$/.test(date) ? date + "T00:00:00" : date;
      const d = new Date(normalized);
      const months = [
        "ene",
        "feb",
        "mar",
        "abr",
        "may",
        "jun",
        "jul",
        "ago",
        "sep",
        "oct",
        "nov",
        "dic"
      ];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    },
    goRouter(name, params = {}) {
      this.$router.push({ name, params });
    },
    openPreview() {
      if (this.$refs.optionsMenu) {
        this.$refs.optionsMenu.handleCommand("invoice-preview");
      }
    },
    invoiceView() {
      this.goRouter("sales.invoices.view", {
        id: this.invoice.id
      });
    },
    onDialogPayLinkClose() {
      this.showDialogPayLink = false;
    },
    onClosePaymentUpDoc() {
      this.showEnablePaylink = false;
    },
    onUpdate() {
      this.$emit("onUpdate");
    },
    onSigningStart() {
      this.isSigning = true;
    },
    onSigningDone({ id, data }) {
      this.isSigning = false;
      if (data) {
        this.$emit("onInvoiceUpdate", { id, data });
      }
    },
    onSigningError() {
      this.isSigning = false;
    },
    onDocDeleted(id) {
      this.$emit("onInvoiceDeleted", id);
    },
    toggleReminders(val) {
      this.$emit("onToggleReminders", {
        invoiceId: this.invoice.id,
        enabled: val
      });
    },
    async getInvoicePaymentLink() {
      try {
        this.$store.dispatch("toggleRequestLoading");

        let response = await paymentService
          .getInvoicePaymentLink({
            invoice_id: this.invoice.id
          })
          .finally(() => {
            this.$store.dispatch("toggleRequestLoading");
          });
        const { success, data, upload_docs = false } = response.data;

        if (success) {
          this.showDialogPayLink = true;
          this.paymentLink = data;
        } else if (upload_docs) {
          this.showEnablePaylink = true;
        }
      } catch (error) {
        return false;
      }
      return false;
    }
  },
  components: {
    StatusReadDocumentsComponent,
    CheckSuccessfully,
    WrapperPaymentLink,
    FormatAmount,
    PaymentUploadDocument,
    ImageIconWoocommerce,
    InvoiceOptionsByStatus
  },
  props: {
    invoice: {
      type: Object,
      default: null
    }
  }
};
</script>

<style lang="scss" scoped>
.reminder-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff3e0;
  color: #e07b00;
  font-size: 14px;
  cursor: default;
  vertical-align: middle;
}

.signing-card--active {
  opacity: 0.45;
  pointer-events: none;
  transition: opacity 0.2s ease;
}

.signing-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 500;
  color: #534ab7;
  background: #eeedfe;
  border-radius: 10px;
  padding: 2px 8px;

  &__spinner {
    width: 10px;
    height: 10px;
    border: 1.5px solid #eeedfe;
    border-top-color: #534ab7;
    border-radius: 50%;
    animation: signing-card-spin 0.7s linear infinite;
  }
}

@keyframes signing-card-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
